import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import cache, { CacheTTL } from '../utils/cache';
import Navbar from '../student/Navbar';
import AssistantSidebar from './AssistantSidebar';
import Loader from '../components/Loader';
import withRoleGuard from '../hoc/withRoleGuard';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaDownload, FaChartLine, FaUserMd, FaInfoCircle, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import AssignStudentModal from './AssignStudentModal';
import { saveAs } from 'file-saver';
import { formatDateEG, formatNumericDateEG, parseDate, isToday } from '../utils/dateUtils';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

// Add Appointment Modal Component
const AddAppointmentModal = ({ isOpen, onClose, onSuccess, universityId }) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    type: '',
    chiefComplaint: '',
    notes: '',
    patientId: '',
    studentId: ''
  });
  const [patients, setPatients] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { token } = useAuth();

  useEffect(() => {
    if (isOpen && universityId) {
      fetchPatients();
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, universityId]);

  // These endpoints are inconsistent: patients come back as a bare array while
  // students come back as { students, pagination }. Assigning the object
  // straight into state made students.map() throw and took the whole app down.
  const toList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data[key])) return data[key];
    return [];
  };

  const fetchPatients = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const data = await cache.getOrSet(
        `appt_modal_patients_${universityId}`,
        async () => {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/patients/university/${encodeURIComponent(universityId)}`,
            config
          );
          return toList(response.data, 'patients');
        },
        CacheTTL.MEDIUM
      );
      setPatients(toList(data, 'patients'));
    } catch (err) {
      setPatients([]);
      setError('Failed to load patients');
    }
  };

  const fetchStudents = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const data = await cache.getOrSet(
        `appt_modal_students_${universityId}`,
        async () => {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/students/university/${encodeURIComponent(universityId)}`,
            config
          );
          return toList(response.data, 'students');
        },
        CacheTTL.LONG
      );
      setStudents(toList(data, 'students'));
    } catch (err) {
      setStudents([]);
      setError('Failed to load students');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Find the selected patient and student
      const selectedPatient = patients.find(p => p.nationalId === formData.patientId);
      const selectedStudent = students.find(s => s.studentId === formData.studentId);

      if (!selectedPatient || !selectedStudent) {
        setError('Please select both patient and student');
        setLoading(false);
        return;
      }

      // Check if student already has an appointment at the same time
      try {
        const appointmentsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/appointments/schedule?university=${encodeURIComponent(universityId)}`,
          config
        );

        const allAppointments = appointmentsResponse.data || [];
        const conflictingAppointment = allAppointments.find(appt => 
          appt.doctor === selectedStudent.studentId &&
          appt.doctorModel === 'Student' &&
          appt.date === formData.date &&
          appt.time === formData.time &&
          appt.status !== 'cancelled'
        );

        if (conflictingAppointment) {
          setError('This student already has an appointment at the selected time. Please choose a different time or student.');
          setLoading(false);
          return;
        }
      } catch (checkErr) {
        // Error checking student availability
        // Continue with appointment creation if we can't check availability
      }

      const appointmentData = {
        date: formData.date,
        time: formData.time,
        type: formData.type,
        chiefComplaint: formData.chiefComplaint,
        notes: formData.notes,
        patient: selectedPatient.nationalId,
        doctor: selectedStudent.studentId,
        doctorModel: 'Student',
        status: 'pending'
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/appointments`,
        appointmentData,
        config
      );

      setFormData({
        date: '',
        time: '',
        type: '',
        chiefComplaint: '',
        notes: '',
        patientId: '',
        studentId: ''
      });

      onSuccess(response.data);
      onClose();
    } catch (err) {
      // Error creating appointment
      setError(err.response?.data?.message || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold" style={{ color: websiteColorPalette.primary }}>
              Add New Appointment
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient *
              </label>
              <select
                name="patientId"
                value={formData.patientId}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Patient</option>
                {patients.map((patient, i) => (
                  <option key={patient.id || `${patient.nationalId}-${i}`} value={patient.nationalId}>
                    {patient.fullName} - {patient.nationalId}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student *
              </label>
              <select
                name="studentId"
                value={formData.studentId}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Student</option>
                {students.map((student, i) => (
                  <option key={student.id || `${student.studentId}-${i}`} value={student.studentId}>
                    {student.name} - {student.studentId}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Procedure Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Procedure</option>
                <option value="consultation">Consultation</option>
                <option value="cleaning">Cleaning</option>
                <option value="filling">Filling</option>
                <option value="extraction">Extraction</option>
                <option value="root_canal">Root Canal</option>
                <option value="crown">Crown</option>
                <option value="bridge">Bridge</option>
                <option value="implant">Implant</option>
                <option value="orthodontics">Orthodontics</option>
                <option value="surgery">Surgery</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chief Complaint *
              </label>
              <textarea
                name="chiefComplaint"
                value={formData.chiefComplaint}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the patient's main complaint..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Chronic diseases options for add patient form
const CHRONIC_DISEASE_OPTIONS = [
  'Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'Arthritis',
  'Cancer', 'Kidney Disease', 'Liver Disease', 'Thyroid Disease',
  'Depression', 'Anxiety', 'Other'
];

const Appointments = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [, setStudents] = useState([]);
  const [dayFilter, setDayFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setRefreshing] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Add Patient Modal state for student-requested appointments
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [pendingConfirmAppointment, setPendingConfirmAppointment] = useState(null);
  const [addPatientForm, setAddPatientForm] = useState({
    nationalId: '',
    fullName: '',
    phoneNumber: '',
    gender: '',
    age: '',
    address: '',
    occupation: '',
    drId: '',
    serialNumber: '',
    registrationDate: new Date().toISOString().split('T')[0],
    notes: '',
    medicalInfo: {
      chronicDiseases: [],
      recentSurgicalProcedures: '',
      currentMedications: '',
      chiefComplaint: '',
      otherChronicDisease: ''
    }
  });
  const [addPatientLoading, setAddPatientLoading] = useState(false);
  
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const fetchData = async (forceRefresh = false) => {
    if (!user || !token) {
      setError('Please log in to view appointments.');
      setLoading(false);
      return;
    }

    try {
      setRefreshing(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const cacheKey = 'assistant_appointments';
      if (forceRefresh) cache.delete(cacheKey);

      const data = await cache.getOrSet(
        cacheKey,
        async () => {
          const appointmentsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/admin/appointments`,
            config
          );
          return appointmentsRes.data || [];
        },
        CacheTTL.SHORT
      );

      setAppointments(data);
      setStudents([]);
      setError('');
    } catch (err) {
      const errorMessage =
        err.response?.status === 404
          ? 'Appointments endpoint not found.'
          : err.response?.status === 401
          ? 'Unauthorized. Please log in again.'
          : err.response?.status === 403
          ? 'Access denied. You do not have permission to view this data.'
          : err.response?.data?.message || 'Failed to load appointments';
      setError(errorMessage);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { fetchData(true); };

  // Handle opening the appointment details modal
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailsModal(true);
  };

  // Handle opening the assign student modal
  const handleAssignClick = (e, appointment) => {
    e.stopPropagation(); // Prevent triggering the row click
    setSelectedAppointment(appointment);
    setShowAssignModal(true);
  };

  // Handle appointment creation success
  const handleAppointmentCreated = (newAppointment) => {
    // Ensure _id is present for React key and styling
    const normalizedAppointment = {
      ...newAppointment,
      _id: newAppointment._id || newAppointment.id
    };
    setAppointments(prev => [normalizedAppointment, ...prev]);
    setSuccessMessage('Appointment created successfully!');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  // Handle updating appointment status
  const handleUpdateStatus = async (e, appointment, newStatus) => {
    e.stopPropagation();
    const appointmentId = appointment._id || appointment.id;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}`,
        { status: newStatus },
        config
      );

      // Update local state
      setAppointments(prev =>
        prev.map(appt =>
          (appt._id || appt.id) === appointmentId ? { ...appt, status: newStatus } : appt
        )
      );

      setSuccessMessage(`Appointment marked as ${newStatus}!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || 'Failed to update appointment status');
    }
  };

  // Handle assigning a student to an appointment
  const handleAssignStudent = async (appointmentId, student) => {
    try {
      console.log('Assigning student:', { appointmentId, student });
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Create the update data
      const updateData = {
        doctor: student.studentId,
        doctorModel: 'Student'
      };

      console.log('Update data:', updateData);
      console.log('API URL:', `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}/assign`);

      // Make the API call to update the appointment
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}/assign`,
        updateData,
        config
      );

      // Get the updated appointment data from the response
      const updatedAppointment = response.data;

      // If we get here, the assignment was successful

      // Find the appointment that was assigned
      const appointmentToAssign = appointments.find(appt => appt._id === appointmentId);

      if (appointmentToAssign) {
        // Create a patient record for the student using appointment information
        try {
          // Extract the national ID from the appointment
          let nationalId = '';
          if (appointmentToAssign.nationalId && appointmentToAssign.nationalId.trim() !== '') {
            nationalId = appointmentToAssign.nationalId.trim();
            console.log('Found nationalId in appointment object:', nationalId);
          } else if (appointmentToAssign.patient && appointmentToAssign.patient.nationalId && appointmentToAssign.patient.nationalId.trim() !== '') {
            nationalId = appointmentToAssign.patient.nationalId.trim();
            console.log('Found nationalId in patient object:', nationalId);
          } else {
            console.log('No valid nationalId found in appointment:', appointmentToAssign);
            // Only generate a temporary ID if we truly have no national ID
            // AND if the appointment doesn't already have a real national ID
            if (!appointmentToAssign.nationalId || appointmentToAssign.nationalId.startsWith('TEMP-')) {
              nationalId = `TEMP-${Date.now()}-${Math.floor(100000 + Math.random() * 900000)}`;
              console.log('Generated temporary nationalId:', nationalId);

              // Update the appointment with the temporary nationalId
              try {
                // Make an API call to update the appointment with the nationalId
                await axios.put(
                  `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentToAssign._id}`,
                  { nationalId },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('Updated appointment with nationalId:', nationalId);
              } catch (err) {
                console.error('Error updating appointment with nationalId:', err);
              }
            } else {
              // Use the existing national ID from the appointment
              nationalId = appointmentToAssign.nationalId;
              console.log('Using existing national ID from appointment:', nationalId);
            }
          }

          // Log the appointment data for debugging
          console.log('Appointment data:', {
            _id: appointmentToAssign._id,
            patient: appointmentToAssign.patient,
            nationalId: nationalId,
            fullName: appointmentToAssign.patient?.fullName || appointmentToAssign.fullName,
            appointmentNationalId: appointmentToAssign.nationalId,
            patientNationalId: appointmentToAssign.patient?.nationalId
          });

          // Extract the patient name
          const patientName = appointmentToAssign.patient?.fullName || appointmentToAssign.fullName || 'Unknown Patient';

          // First, check if the student already has this patient in their list
          let patientExists = false;
          let existingPatientId = null;

          if (nationalId) {
            try {
              // Get all patients for this student
              const studentsPatients = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/patients/student/${student.studentId}`,
                config
              );

              // Check if any patient has the same national ID
              const existingPatient = studentsPatients.data.find(
                patient => patient.nationalId === nationalId
              );

              if (existingPatient) {
                patientExists = true;
                existingPatientId = existingPatient._id;
                console.log(`Found existing patient ${existingPatient.fullName} (${nationalId}) for student ${student.studentId}`);

                // Set flag for success message
                window.patientRecordCreated = false;
              }
            } catch (checkErr) {
              console.log('Error checking student patients:', checkErr.message);
              // Continue to create a new patient if check fails
            }
          }

          if (!patientExists) {
            // Check if we have a valid national ID - only generate temp if we truly don't have one
            if (!nationalId || nationalId.trim() === '' || nationalId.startsWith('TEMP-')) {
              console.warn('No valid national ID found for patient. Using a placeholder value.');
              nationalId = `TEMP-${Date.now()}-${Math.floor(100000 + Math.random() * 900000)}`; // Generate a temporary ID if none exists
            } else {
              console.log('Using existing national ID:', nationalId);
            }

            // Extract patient information from the appointment
            const patientData = {
              fullName: patientName,
              nationalId: nationalId,
              phoneNumber: appointmentToAssign.patient?.phoneNumber || appointmentToAssign.phoneNumber || '000-000-0000',
              gender: appointmentToAssign.patient?.gender || appointmentToAssign.gender || 'other',
              age: appointmentToAssign.patient?.age || appointmentToAssign.age || 0,
              address: appointmentToAssign.patient?.address || appointmentToAssign.address || '',
              occupation: appointmentToAssign.patient?.occupation || appointmentToAssign.occupation || '',
              drId: student.studentId, // This is required by the backend - use studentId, not _id
              medicalInfo: {
                chiefComplaint: appointmentToAssign.chiefComplaint || '',
                chronicDiseases: [],
                recentSurgicalProcedures: '',
                currentMedications: ''
              }
            };

            console.log('Creating patient with drId (studentId):', student.studentId);

            console.log('Creating patient with the following data:', {
              fullName: patientData.fullName,
              nationalId: patientData.nationalId,
              phoneNumber: patientData.phoneNumber,
              gender: patientData.gender,
              age: patientData.age,
              drId: patientData.drId
            });

            // Make API call to create a patient record
            try {
              console.log('Creating patient with data:', patientData);
              const createResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/patients`,
                patientData,
                config
              );

              console.log('Patient creation response:', createResponse.data);

              if (createResponse.data.patient) {
                console.log(`Created patient record for ${patientData.fullName} assigned to student ${student.studentId}`);
                window.patientRecordCreated = true;

                // Store the new patient ID for appointment association
                existingPatientId = createResponse.data.patient._id;
              } else {
                console.log('Patient created but no patient data returned');
                window.patientRecordCreated = true;
              }
            } catch (createErr) {
              console.error('Error creating patient:', createErr.response?.data || createErr.message);

              // Check if this is a duplicate key error (patient already exists)
              if (createErr.response?.data?.message?.includes('already exists')) {
                console.log(`Patient with nationalId ${nationalId} already exists in the database`);
                window.patientRecordCreated = false;
              } else {
                // Set error flag but don't re-throw
                window.patientRecordError = true;
                window.patientRecordCreated = false;
              }
            }
          } else {
            // Set flag for success message
            window.patientRecordCreated = false;
            console.log(`Patient record already exists for ${patientName} with student ${student.studentId}`);
          }

          // If we have a patient ID and the appointment has a patient field, update the appointment
          if (existingPatientId && appointmentToAssign.patient) {
            try {
              // Update the appointment to link it to this patient
              await axios.put(
                `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentToAssign._id}/patient`,
                { patientId: existingPatientId },
                config
              );

              console.log(`Updated appointment ${appointmentToAssign._id} to link to patient ${existingPatientId}`);
            } catch (updateErr) {
              console.error('Error updating appointment with patient:', updateErr.message);
            }
          }
        } catch (patientErr) {
          console.error('Error creating patient record:', patientErr.response?.data || patientErr.message);
          // Set flag to indicate error
          window.patientRecordCreated = false;
          window.patientRecordError = true;
          // We don't throw here to avoid interrupting the appointment assignment process
          // Just log the error and continue
        }
      }

      // Update the appointments list with the updated appointment
      const updatedAppointments = appointments.map(appt =>
        appt._id === appointmentId
          ? {
              ...appt,
              doctor: student.studentId,
              doctorModel: 'Student',
              studentName: student.name,
              studentId: student.studentId
            }
          : appt
      );

      setAppointments(updatedAppointments);

      // If this is the currently selected appointment, update it too
      if (selectedAppointment && selectedAppointment._id === appointmentId) {
        setSelectedAppointment({
          ...selectedAppointment,
          doctor: student.studentId,
          doctorModel: 'Student',
          studentName: student.name,
          studentId: student.studentId
        });
      }

      // Show success message with information about patient record
      let successMsg = `Successfully assigned appointment to ${student.name}`;

      // Add information about the patient record if we have it
      if (appointmentToAssign) {
        const patientName = appointmentToAssign.patient?.fullName || appointmentToAssign.fullName || 'Unknown Patient';

        // We'll set these variables in the try/catch block for patient creation
        if (typeof window.patientRecordCreated !== 'undefined') {
          if (window.patientRecordError) {
            successMsg += ` (appointment assigned, but there was an error creating patient record for ${patientName})`;
          } else if (window.patientRecordCreated === true) {
            successMsg += ` and created new patient record for ${patientName}`;
          } else {
            successMsg += ` (existing patient record found for ${patientName})`;
          }
          // Reset the flags
          delete window.patientRecordCreated;
          delete window.patientRecordError;
        }
      }

      setSuccessMessage(successMsg);
      setTimeout(() => setSuccessMessage(''), 5000);

      fetchData(true);

      return updatedAppointment;
    } catch (err) {
      console.error('Error assigning student:', err.response?.data || err.message);
      throw new Error(err.response?.data?.message || 'Failed to assign student');
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, navigate]);

  const downloadAppointments = () => {
    if (filteredAppointments.length === 0) {
      setError('No appointments to download.');
      return;
    }

    try {
      const headers = ['Date', 'Time', 'Patient', 'Procedure', 'Status', 'Student', 'University'];
      const data = filteredAppointments.map((appt) => [
        formatNumericDateEG(appt.date),
        appt.time || 'N/A',
        appt.patient?.fullName || appt.fullName || 'Unknown',
        appt.treatment || appt.type || 'N/A',
        appt.status,
        appt.studentName || appt.doctor || 'N/A',
        appt.university || user.university || user.affiliation?.id || 'N/A',
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...data.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `appointments-${new Date().toISOString().slice(0, 10)}.csv`);

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg';
      successMessage.textContent = 'Appointments exported successfully!';
      document.body.appendChild(successMessage);

      // Remove success message after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
    } catch (err) {
      console.error('Error downloading appointments:', err);
      setError('Failed to download appointments. Please try again.');
    }
  };

  const filterAppointments = () => {
    // Copy before sorting: `appointments` is state and sort() mutates in place.
    let filtered = [...appointments];

    // Filter by date
    if (dayFilter === 'today') {
      filtered = filtered.filter((a) => isToday(a.date));
    } else if (dayFilter === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      filtered = filtered.filter((a) => {
        const apptDate = parseDate(a.date);
        if (!apptDate) return false;
        return apptDate.toDateString() === tomorrow.toDateString();
      });
    } else if (dayFilter === 'week') {
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() + 7);
      filtered = filtered.filter((a) => {
        const apptDate = parseDate(a.date);
        if (!apptDate) return false;
        const today = new Date();
        return apptDate >= today && apptDate <= weekEnd;
      });
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    // Sort by date and time (newest first). Parse each date once instead of
    // re-parsing it on every comparison - with thousands of appointments the
    // repeated parsing was the slowest part of every render.
    return filtered
      .map((appt) => ({ appt, time: parseDate(appt.date)?.getTime() ?? null }))
      .sort((a, b) => {
        if (a.time === null || b.time === null) return 0;
        if (a.time !== b.time) return b.time - a.time; // Newest first
        return (b.appt.time || '').localeCompare(a.appt.time || ''); // Newest time first
      })
      .map((entry) => entry.appt);
  };

  // Recompute only when the data or the filters actually change, not on every
  // unrelated state update (modals, selections, ...).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredAppointments = useMemo(filterAppointments, [appointments, dayFilter, statusFilter]);
  
  // Pagination logic
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAppointments = filteredAppointments.slice(startIndex, startIndex + itemsPerPage);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dayFilter, statusFilter]);
  
  // Handle confirm click - check if student-requested appointment needs patient registration
  const handleConfirmClick = (e, appointment) => {
    e.stopPropagation();
    
    // Check if this is a student-requested appointment (isStudentRequest flag)
    if (appointment.isStudentRequest) {
      // Pre-fill the form with available data
      setAddPatientForm({
        nationalId: appointment.nationalId || '',
        fullName: appointment.patientName || appointment.patient?.fullName || appointment.fullName || '',
        phoneNumber: appointment.phoneNumber || appointment.patient?.phoneNumber || '',
        gender: appointment.gender || appointment.patient?.gender || '',
        age: appointment.age || appointment.patient?.age || '',
        address: appointment.address || appointment.patient?.address || '',
        occupation: appointment.occupation || appointment.patient?.occupation || '',
        drId: appointment.studentId || appointment.doctor || '',
        serialNumber: '',
        registrationDate: new Date().toISOString().split('T')[0],
        notes: '',
        medicalInfo: {
          chronicDiseases: [],
          recentSurgicalProcedures: '',
          currentMedications: '',
          chiefComplaint: appointment.chiefComplaint || '',
          otherChronicDisease: ''
        }
      });
      setPendingConfirmAppointment(appointment);
      setShowAddPatientModal(true);
    } else {
      // Regular appointment - just confirm it
      handleUpdateStatus(e, appointment, 'completed');
    }
  };
  
  // Handle add patient form input change
  const handleAddPatientInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('medical-')) {
      const field = name.replace('medical-', '');
      setAddPatientForm(prev => ({
        ...prev,
        medicalInfo: { ...prev.medicalInfo, [field]: value }
      }));
    } else {
      setAddPatientForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle chronic disease checkbox change
  const handleChronicDiseaseChange = (e) => {
    const { value, checked } = e.target;
    setAddPatientForm(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        chronicDiseases: checked
          ? [...prev.medicalInfo.chronicDiseases, value]
          : prev.medicalInfo.chronicDiseases.filter(d => d !== value)
      }
    }));
  };
  
  // Generate unique national ID
  const generateUniqueNationalId = async () => {
    let isUnique = false;
    let nationalId = '';
    let attempts = 0;
    
    while (!isUnique && attempts < 10) {
      // Generate: TEMP-timestamp-random6digits (virtually guaranteed unique)
      nationalId = `TEMP-${Date.now()}-${Math.floor(100000 + Math.random() * 900000)}`;
      
      try {
        // Check if this ID already exists using public endpoint
        await axios.get(`${process.env.REACT_APP_API_URL}/api/patients/public/${nationalId}`);
        // If we get here, patient exists - try again
        isUnique = false;
      } catch (err) {
        // 404 means patient doesn't exist, so ID is unique
        if (err.response?.status === 404) {
          isUnique = true;
        } else {
          // Other error - assume unique and proceed
          isUnique = true;
        }
      }
      attempts++;
    }
    
    return nationalId;
  };
  
  // Submit add patient form and confirm appointment
  const handleAddPatientSubmit = async (e) => {
    e.preventDefault();
    setAddPatientLoading(true);
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Generate unique temp national ID if not provided
      let nationalId = addPatientForm.nationalId.trim();
      if (!nationalId) {
        nationalId = await generateUniqueNationalId();
      }
      
      // Parse age properly - ensure it's a valid number
      const ageValue = addPatientForm.age ? parseInt(addPatientForm.age, 10) : 0;
      
      // Create patient data
      const patientData = {
        nationalId,
        fullName: addPatientForm.fullName,
        phoneNumber: addPatientForm.phoneNumber || '000-000-0000',
        gender: addPatientForm.gender || 'other',
        age: ageValue,
        address: addPatientForm.address || '',
        occupation: addPatientForm.occupation || '',
        drId: addPatientForm.drId,
        serialNumber: addPatientForm.serialNumber || '',
        registrationDate: addPatientForm.registrationDate,
        notes: addPatientForm.notes || '',
        medicalInfo: addPatientForm.medicalInfo
      };
      
      console.log('Creating patient with age:', ageValue, 'from form value:', addPatientForm.age);
      
      // Create the patient
      await axios.post(`${process.env.REACT_APP_API_URL}/api/patients`, patientData, config);
      
      // Update appointment status to completed and remove isStudentRequest flag
      const appointmentId = pendingConfirmAppointment._id || pendingConfirmAppointment.id;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}`,
        { status: 'completed', isStudentRequest: false, nationalId },
        config
      );
      
      // Update local state
      setAppointments(prev =>
        prev.map(appt =>
          (appt._id || appt.id) === appointmentId 
            ? { ...appt, status: 'completed', isStudentRequest: false, nationalId } 
            : appt
        )
      );
      
      setSuccessMessage('Patient registered and appointment confirmed!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Close modal and reset
      setShowAddPatientModal(false);
      setPendingConfirmAppointment(null);
      setAddPatientForm({
        nationalId: '',
        fullName: '',
        phoneNumber: '',
        gender: '',
        age: '',
        address: '',
        occupation: '',
        drId: '',
        serialNumber: '',
        registrationDate: new Date().toISOString().split('T')[0],
        notes: '',
        medicalInfo: {
          chronicDiseases: [],
          recentSurgicalProcedures: '',
          currentMedications: '',
          chiefComplaint: '',
          otherChronicDisease: ''
        }
      });
    } catch (err) {
      console.error('Error adding patient:', err);
      setError(err.response?.data?.message || 'Failed to register patient');
    } finally {
      setAddPatientLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <AssistantSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            {/* No entrance animation around the appointment data.
                Framer Motion writes `opacity: 0` inline and only clears it once
                the animation actually runs, so anything that stops it running -
                a backgrounded tab, a long main-thread block, a viewport trigger
                that never fires - leaves the whole list rendered but invisible.
                The rows were still in the DOM and still clickable, which is
                exactly what made it look like nothing had loaded. */}
            <div>
              <div className="mb-6 sm:mb-8">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[${websiteColorPalette.primary}] mb-1`}>Appointments</h1>
                <p className={`text-[${websiteColorPalette.text}]`}>Manage and view all appointments in your university</p>
              </div>

              <div className="space-y-6 sm:space-y-8">
                <div
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  <div className="border-b border-gray-200">
                    <div className="py-3 sm:py-4 px-4 sm:px-6 text-center font-medium text-xs sm:text-sm"
                      style={{
                        color: websiteColorPalette.primary,
                        borderBottom: `2px solid ${websiteColorPalette.primary}`
                      }}
                    >
                      Appointment Management
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                      <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                        <FaCalendarAlt className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                        Appointment List
                      </h2>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <FaChartLine className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                        <span className="font-medium text-sm sm:text-base" style={{ color: websiteColorPalette.primary }}>Total: {appointments.length}</span>
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="px-3 sm:px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors flex items-center text-xs sm:text-sm"
                          title="Add Appointment"
                        >
                          <FaPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          Add Appointment
                        </button>
                        <button
                          onClick={downloadAppointments}
                          className="px-3 sm:px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors flex items-center text-xs sm:text-sm"
                          title="Download CSV"
                        >
                          <FaDownload className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          Download CSV
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <select
                        value={dayFilter}
                        onChange={(e) => setDayFilter(e.target.value)}
                        className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="tomorrow">Tomorrow</option>
                        <option value="week">This Week</option>
                      </select>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                            <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Procedure</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedAppointments.map((appt, rowIndex) => (
                            <tr
                              key={appt._id || appt.id || `appt-${rowIndex}`}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleAppointmentClick(appt)}
                            >
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                                {formatDateEG(appt.date, {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">{appt.time || 'N/A'}</td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                                {appt.patient?.fullName || appt.fullName || 'Unknown'}
                                <div className="text-xs text-gray-400">
                                  ID: {appt.nationalId || appt.patient?.nationalId || 'N/A'}
                                </div>
                              </td>
                              <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                                {appt.treatment || appt.type || 'N/A'}
                                {appt.chiefComplaint && <div className="text-xs text-gray-400 mt-1">Chief complaint: {appt.chiefComplaint}</div>}
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 sm:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    appt.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : appt.status === 'cancelled'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {appt.status}
                                </span>
                              </td>
                              <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                                {appt.studentName || appt.doctor || 'N/A'}
                                {appt.studentId && <div className="text-xs text-gray-400">ID: {appt.studentId}</div>}
                                {appt.doctorModel && <div className="text-xs text-gray-400">Type: {appt.doctorModel}</div>}
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                                <div className="flex space-x-1 sm:space-x-2">
                                  <button
                                    onClick={(e) => handleConfirmClick(e, appt)}
                                    className="transition-colors p-1 rounded-full hover:bg-green-50"
                                    style={{ color: '#22c55e' }}
                                    title={appt.isStudentRequest ? "Confirm & Register Patient" : "Mark as Completed"}
                                    disabled={appt.status === 'completed'}
                                  >
                                    <FaCheck className={`h-4 w-4 sm:h-5 sm:w-5 ${appt.status === 'completed' ? 'opacity-30' : ''}`} />
                                  </button>
                                  <button
                                    onClick={(e) => handleUpdateStatus(e, appt, 'cancelled')}
                                    className="transition-colors p-1 rounded-full hover:bg-red-50"
                                    style={{ color: '#ef4444' }}
                                    title="Mark as Cancelled"
                                    disabled={appt.status === 'cancelled'}
                                  >
                                    <FaTimes className={`h-4 w-4 sm:h-5 sm:w-5 ${appt.status === 'cancelled' ? 'opacity-30' : ''}`} />
                                  </button>
                                  <button
                                    onClick={(e) => handleAssignClick(e, appt)}
                                    className="transition-colors p-1 rounded-full hover:bg-blue-50"
                                    style={{
                                      color: websiteColorPalette.primary
                                    }}
                                    title="Assign to Student"
                                  >
                                    <FaUserMd className="h-4 w-4 sm:h-5 sm:w-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAppointmentClick(appt);
                                    }}
                                    className="transition-colors p-1 rounded-full hover:bg-gray-100"
                                    style={{
                                      color: '#4b5563'
                                    }}
                                    title="View Details"
                                  >
                                    <FaInfoCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}

                          {paginatedAppointments.length === 0 && (
                            <tr>
                              <td colSpan={7} className="px-6 py-12 text-center">
                                <FaCalendarAlt className="h-7 w-7 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm text-gray-500">
                                  No appointments match the selected filters
                                </p>
                                {(dayFilter !== 'all' || statusFilter !== '') && (
                                  <button
                                    onClick={() => { setDayFilter('all'); setStatusFilter(''); }}
                                    className="mt-2 text-sm font-medium hover:underline"
                                    style={{ color: websiteColorPalette.primary }}
                                  >
                                    Clear filters
                                  </button>
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredAppointments.length)} of {filteredAppointments.length} appointments
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Previous
                          </button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`px-3 py-1 rounded-lg text-sm ${
                                    currentPage === pageNum
                                      ? 'bg-[#0077B6] text-white'
                                      : 'border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in-out"
          style={{ backgroundColor: websiteColorPalette.accent }}>
          {successMessage}
        </div>
      )}

      {/* Appointment Details Modal */}
      <AppointmentDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        appointment={selectedAppointment}
      />

      {/* Assign Student Modal */}
      <AssignStudentModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        appointment={selectedAppointment}
        onAssign={handleAssignStudent}
      />

      {/* Add Appointment Modal */}
      <AddAppointmentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAppointmentCreated}
        universityId={user?.university || user?.affiliation?.id}
      />

      {/* Add Patient Modal for Student-Requested Appointments */}
      {showAddPatientModal && pendingConfirmAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-[#0077B6] to-[#20B2AA]">
              <h2 className="text-lg sm:text-xl font-semibold text-white">Register Patient & Confirm Appointment</h2>
              <button
                onClick={() => {
                  setShowAddPatientModal(false);
                  setPendingConfirmAppointment(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleAddPatientSubmit} className="p-4 sm:p-6 space-y-6">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <strong>Student Request:</strong> This appointment was requested by a student. Please complete the patient registration to confirm.
              </div>
              
              <h3 className="text-lg font-semibold text-[#0077B6] mb-4 border-b border-gray-200 pb-2">
                Patient Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                  <input
                    type="text"
                    name="fullName"
                    value={addPatientForm.fullName}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">National ID</label>
                  <input
                    type="text"
                    name="nationalId"
                    value={addPatientForm.nationalId}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={addPatientForm.phoneNumber}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    name="gender"
                    value={addPatientForm.gender}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={addPatientForm.age}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={addPatientForm.address}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={addPatientForm.occupation}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={addPatientForm.serialNumber}
                    onChange={handleAddPatientInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Student</label>
                  <input
                    type="text"
                    value={addPatientForm.drId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    readOnly
                  />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-[#0077B6] mb-4 border-b border-gray-200 pb-2">
                Medical Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chief Complaint</label>
                  <textarea
                    name="medical-chiefComplaint"
                    value={addPatientForm.medicalInfo.chiefComplaint}
                    onChange={handleAddPatientInputChange}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Medications</label>
                  <textarea
                    name="medical-currentMedications"
                    value={addPatientForm.medicalInfo.currentMedications}
                    onChange={handleAddPatientInputChange}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chronic Diseases</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CHRONIC_DISEASE_OPTIONS.map((disease) => (
                      <label key={disease} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          value={disease}
                          checked={addPatientForm.medicalInfo.chronicDiseases.includes(disease)}
                          onChange={handleChronicDiseaseChange}
                          className="rounded border-gray-300 text-[#0077B6] focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{disease}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPatientModal(false);
                    setPendingConfirmAppointment(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addPatientLoading}
                  className="px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors disabled:opacity-50"
                >
                  {addPatientLoading ? 'Registering...' : 'Register Patient & Confirm'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default withRoleGuard(['assistant'])(Appointments);
